import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faInbox } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../shared/notify";
import eventEmitter from "../../shared/events";

import { MeasurementsDisplay } from "./components/measurements-display";
import AttributeTable from "../attributes/attribute-table";
import NotificationBar from "../../components/notification-bar";
import TaskMessages from "../../components/notification-bar/task-messages";
import TaskNav from "../task-nav/task-nav";
import TaskStatus from "../task-status";
import Thumbnail from "../image-stream/thumbnail";
import ProductMatchModal from "./components/product-match-modal";
import DuplicateCheck from "../duplicate-check";

import { getTasksByContextKey } from "../task-list/lib/taskData";
import { validateTask } from "../../shared/taskState";
import { TaskContext } from "../../contexts/task";
import { useAttributesValidity } from "../../shared/hooks";
import { useWindowVisible } from "../table/lib/hooks";
import { getShipment } from "../bin-label/lib/binLabelData";
import { DATE_FNS_SHORT_DATE_FORMAT } from "../../shared/constants";

import QANav from "../task-nav/qa-nav";
import NoteLauncher from "../notes";
import AddAttributeSearch from "../coding/components/add-attribute-search";
import ImageReview from "./components/image-review";
import { useAlert } from "hooks/useAlert";
import { SettingsContext } from "contexts/settings";

import "./qa-list.scss";
import { TaskType } from "shared/taskTypeMap";
import { getPhotos } from "../image-editing/lib/imageData";
import { SHIPMENT_TYPE_MAP } from "../shipments/lib/constants";
import { SidePanel } from "../side-panel/side-panel";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ToggleSwitch } from "../form-inputs/custom/toggle-switch";
import { useShipmentProductFeatures } from "../../shared/hooks/use-shipment-product-features";
import { cesFeatureGuids } from "../../contexts/settings/lib/constants";
import { AttributesContext } from "../../contexts/attributes";
import { attributeUUID } from "../../shared/attribute-uuid";
import { statusDescription, TaskStatus as TaskStatusType } from "../../shared/constants/task-status";
import classnames from "classnames";

const FinalQAIndex = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { toggleAlert } = useAlert();
    const { data } = useContext(AttributesContext);
    const { settings, hasReadOnlyPermission } = useContext(SettingsContext);
    const taskKey = params.id;
    const { task } = useContext(TaskContext);
    const attributesValidity = useAttributesValidity();
    const [productMatchValid, setProductMatchValid] = useState(false);
    const isWindowVisible = useWindowVisible();
    const [state, setState] = useState({
        isLoaded: false,
        taskMessage: null,
        tasks: [],
        tasksValid: false,
        shots: [],
    });
    const [showReferences, setShowReferences] = useState(true);
    const consumerProductName = data[attributeUUID.syndigoCustomProductName];
    const { featureName: costcoFeatureName } = useShipmentProductFeatures(cesFeatureGuids.COSTCOSPSUP, task.context.shipmentProductKey);
    const isReadOnlyNotes = useViewOnlyPermissions("CreateNote", task.sourcePage);
    const isReadOnlyCompleteTask = useViewOnlyPermissions("CompleteTask", task.sourcePage);
    const isReadOnlyAdd = useViewOnlyPermissions("AddAttribute", task.sourcePage);
    const isReadOnlyEdit = useViewOnlyPermissions("EditAttribute", task.sourcePage);
    const isReadOnlyDelete = useViewOnlyPermissions("DeleteAttribute", task.sourcePage);
    const onHoldLabel = `This product is On Hold${
        state.shipmentData && state.shipmentData.holdReleaseDate
            ? " until " + format(new Date(state.shipmentData.holdReleaseDate), DATE_FNS_SHORT_DATE_FORMAT)
            : " with no release date set"
    }`;
    const statusLabel = statusDescription.find(({ value }) => value === task.state);
    const isBulkRejectPending = task?.state === TaskStatusType.BULK_REJECT_PENDING;
    const fqaContainer = classnames({
        "disable-fqa": isBulkRejectPending,
    });
    const linkButtonClassNames = classnames("button", { "-disabled": isBulkRejectPending });

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert();
        }
    }, [settings.globalNotifications.messages.length]);

    useEffect(() => {
        eventEmitter.on("REFRESH_PAGE", refreshTaskList);

        return () => eventEmitter.removeListener("REFRESH_PAGE", refreshTaskList);
    }, []);

    useEffect(() => {
        if (isWindowVisible) {
            buildTaskData();
            eventEmitter.emit("REFRESH_ATTRIBUTES");
        }
    }, [isWindowVisible]);

    const buildTaskData = async () => {
        const tasks = await getTasksByContextKey(task.taskContextKey);
        const shipmentData = await getShipment(task.context.shipmentKey);
        const shots = await getPhotos(task.context.photographyWipKey, { defaultReturn: [] });

        setState({
            isLoaded: true,
            shipmentData,
            taskMessage: task.messages ? task.messages.finalQA : null,
            tasks,
            tasksValid: tasks.filter((a) => a.taskType !== TaskType.FINAL_QA && a.isCompleted === false).length === 0,
            shots,
        });
    };

    const updateProductMatchValid = (bool) => {
        setProductMatchValid(bool);
    };

    const refreshTaskList = () => {
        buildTaskData();
    };

    const navigateBack = (e) => {
        e.preventDefault();
        navigate("/task-list/finalqa");
    };

    const validateProduct = async (override = false) => {
        let isValid = true;

        if (!attributesValidity.isValid) {
            notify.error("Attributes are not valid");
            isValid = false;
        }

        if (!state.tasksValid) {
            notify.error("Not all tasks are completed");
            isValid = false;
        }

        if (!productMatchValid) {
            notify.error("Product Matches require attention");
            isValid = false;
        }

        if (isValid) {
            const serverValidate = await validateTask(taskKey, override, { passthrough: true, displayError: true });

            if (serverValidate.errors && serverValidate.errors.length > 0) {
                eventEmitter.emit("REFRESH_ATTRIBUTES");
                refreshTaskList();

                return false;
            }

            if (serverValidate && serverValidate.attributeValueLogicalErrors) {
                return serverValidate;
            }

            if (!override && serverValidate.overridableErrors && serverValidate.overridableErrors.length > 0) {
                const displayErrors = serverValidate.overridableErrors.join(", ");
                const confirmOverride = await notify.confirm(`${displayErrors}`);

                if (confirmOverride) {
                    return validateProduct(true);
                } else {
                    return false;
                }
            }
        }

        return isValid;
    };

    const onComplete = async () => {
        navigate("/task-list/finalqa");
    };

    const allValid = attributesValidity.isValid && state.tasksValid;

    if (!state.isLoaded) {
        return null;
    }

    return (
        <div className="flex">
            <SidePanel />
            <div className="container flex-1">
                <TaskNav
                    taskKey={taskKey}
                    hasIMDropboxContent={task.hasIMDropboxContent}
                    isSampleProduct={task.isSampleUpc}
                    linkedTaskContextKey={task.context.linkedTaskContextKey}
                >
                    <div className="button-group">
                        <button
                            className="button"
                            onClick={navigateBack}
                        >
                            Back
                        </button>
                        <h1 className="txt-center">
                            Final QA - {task.fullUpc}
                            {task.context.shipmentType >= 0 && (
                                <span className="ml-1 mr-1">({SHIPMENT_TYPE_MAP[task.context.shipmentType].label})</span>
                            )}
                        </h1>

                        {task.context.matchedProductTaskContextKey && <DuplicateCheck matchType={task.context.productMatchType} />}
                        <NoteLauncher
                            settings={{
                                shipmentKey: task.context.shipmentKey,
                                shipmentProductKey: task.context.shipmentProductKey,
                            }}
                            clientCode={task.context.cxHClientCode}
                            binTrackingNumber={[task.context.binTrackingNumber]}
                            noteType="Product"
                            taskType={TaskType.FINAL_QA}
                            sourcePage={task.sourcePage}
                            isReadOnly={isReadOnlyNotes}
                        />
                        <a
                            href={`/shipment-status/${task.shipmentKey}#${task.context.key}`}
                            className={linkButtonClassNames}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Shipment Status"
                        >
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </a>
                        <a
                            href={`/shipment-editor/${task.shipmentKey}`}
                            className={linkButtonClassNames}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Edit Shipment"
                        >
                            <FontAwesomeIcon icon={faInbox} />
                        </a>
                        {!task.isCompleted && (
                            <QANav
                                taskId={taskKey}
                                onComplete={onComplete}
                                validate={validateProduct}
                                isQA={false}
                                isDirty={!allValid}
                                acceptPayload={{ label: "Finalize Product" }}
                                sourcePage={task.sourcePage}
                                isReadOnly={isReadOnlyCompleteTask || isBulkRejectPending}
                            />
                        )}
                    </div>
                </TaskNav>
                {costcoFeatureName && (
                    <NotificationBar
                        variant="small"
                        type="info"
                    >
                        {costcoFeatureName}
                    </NotificationBar>
                )}
                {state.shipmentData.isOnHold && (
                    <NotificationBar
                        type="warning"
                        message={onHoldLabel}
                    />
                )}
                {task.hasPremiumEditing && <NotificationBar message="This product has been flagged for premium editing." />}
                <TaskMessages message={state.taskMessage} />
                {isBulkRejectPending && (
                    <div className="row">
                        <div className="column -span3" />
                        <div className="column -span9">
                            <NotificationBar type="error">
                                <div>
                                    A bulk rejection is currently being processed for this UPC. No changes can be made to this UPC until the bulk
                                    rejection process is complete.
                                </div>
                            </NotificationBar>
                        </div>
                    </div>
                )}
                <div className={fqaContainer}>
                    <div className="flex">
                        <div className="flex-1 p-10">
                            <div className="product-images gap-8">
                                <div className="product-image">
                                    <h4>Current Product</h4>
                                    <Thumbnail
                                        photoKey={task.context.photographyWipKey}
                                        shipmentKey={task.shipmentKey}
                                        shipmentProductKey={task.shipmentProductKey}
                                        size="small"
                                        disablePopout
                                    />
                                </div>
                                <ProductMatchModal
                                    taskKey={taskKey}
                                    isComplete={task.isCompleted}
                                    setIsValid={updateProductMatchValid}
                                    shots={state.shots}
                                />
                                <div className="flex-column justify-content-between">
                                    {consumerProductName && <h3 className="m-t-0 txt-wrap">{consumerProductName}</h3>}
                                    {statusLabel && (
                                        <div>
                                            <strong>Status: </strong>
                                            {statusLabel.text}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex p-10 justify-content-end align-items-end">
                            <MeasurementsDisplay
                                measuringWipKey={task.context.measuringWipKey}
                                isFinalized={task.isCompleted}
                            />
                        </div>
                    </div>

                    <ImageReview shots={state.shots} />

                    <br />
                    {state.tasks.length > 0 && (
                        <TaskStatus
                            contextKey={task.taskContextKey}
                            imagesNeedClientApproval={task.context.imagesNeedClientApproval}
                            tasks={state.tasks}
                            refreshTaskList={refreshTaskList}
                            isFinalized={task.isCompleted}
                            currentTask={TaskType.FINAL_QA}
                            shipmentKey={task.context.shipmentKey}
                            shipmentProductKey={task.context.shipmentProductKey}
                            shipmentData={state.shipmentData}
                            sourcePage={task.sourcePage}
                            hasReadOnlyPermission={hasReadOnlyPermission}
                        />
                    )}

                    <div>
                        <div className="flex justify-content-end">
                            <div className="flex m-r-16 m-b-10 align-items-center">
                                <ToggleSwitch
                                    variant="small"
                                    name="showReferences"
                                    value={showReferences}
                                    updateOnChange={(name, value) => setShowReferences(value)}
                                />
                                <div className="m-l-4">View Reference Products</div>
                            </div>
                            {!task.isCompleted && !isReadOnlyAdd && (
                                <AddAttributeSearch
                                    cssClass="flex"
                                    wipKey={task.context.codingWipKey}
                                    isSuccess={true}
                                    taskType={TaskType.FINAL_QA}
                                />
                            )}
                        </div>
                        <AttributeTable
                            readOnlyOverride={true}
                            showReferences={showReferences}
                            isReadOnlyAdd={isReadOnlyAdd}
                            isReadOnlyEdit={isReadOnlyEdit}
                            isReadOnlyDelete={isReadOnlyDelete}
                            taskType={TaskType.FINAL_QA}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinalQAIndex;
